import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Initials, PanelCard, ShowMoreFooter } from '../../../components/ui';
import type { RosterStudent } from '../sheetStats';
import { formatRoll } from '../../../lib/roll';

const PREVIEW = 5;
const R = 34;
const CIRC = 2 * Math.PI * R;

interface Props {
  /** The exam's audience. */
  roster: RosterStudent[];
  /** Audience members with no sheet yet. */
  missing: RosterStudent[];
}

/** "Who's still missing": a ring of sheets received over the audience, and the students without one. */
export default function MissingCard({ roster, missing }: Props) {
  const [showAll, setShowAll] = useState(false);
  const received = roster.length - missing.length;
  const frac = roster.length > 0 ? received / roster.length : 0;
  const rows = showAll ? missing : missing.slice(0, PREVIEW);
  return (
    <PanelCard
      title="Who's still missing"
      actions={<Badge tone="neutral">{missing.length} {missing.length === 1 ? 'student' : 'students'}</Badge>}>
      <div className="mb-2 flex items-center gap-4">
        <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true" className="flex-none">
          <circle cx="42" cy="42" r={R} fill="none" strokeWidth="10" className="stroke-track" />
          <circle cx="42" cy="42" r={R} fill="none" strokeWidth="10" strokeLinecap="round" className="stroke-slate"
            strokeDasharray={`${CIRC * frac} ${CIRC}`} transform="rotate(-90 42 42)" />
        </svg>
        <div>
          <div className="font-mono text-[30px] font-semibold tracking-[-0.03em] text-ink">
            {received}<span className="text-base font-medium text-muted"> / {roster.length}</span>
          </div>
          <div className="text-[13px] text-muted">sheets received</div>
        </div>
      </div>
      {missing.length === 0 ? (
        <p className="py-2 text-sm text-muted">Every student in the audience has a sheet.</p>
      ) : (
        <ul>
          {rows.map((m) => (
            <li key={m.id} className="border-b border-hairline last:border-b-0">
              <Link to={`/students/profile/${m.id}`} className="flex items-center gap-3 py-[11px] no-underline">
                <Initials name={m.name} />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-ink">{m.name}</span>
                  <span className="text-xs text-muted">
                    {[m.roll != null ? `Roll ${formatRoll(m.roll)}` : 'No roll number', m.section].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="text-xs text-muted">No sheet</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {missing.length > PREVIEW && (
        <ShowMoreFooter className="mt-2" shown={rows.length} total={missing.length} expanded={showAll} onToggle={() => setShowAll((v) => !v)} />
      )}
    </PanelCard>
  );
}
